import React, { useEffect, useState } from "react";
import { Gift, Check } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatAUD } from "@/lib/media";
import { toast } from "sonner";

export default function BookTeeUpsell({ productId }) {
  const [cfg, setCfg] = useState(null);
  const [size, setSize] = useState(null);
  const [colour, setColour] = useState(null);
  const { addBundle } = useStore();

  useEffect(() => { api.get("/upsell/book-tee").then(({ data }) => setCfg(data)).catch(() => setCfg({ active: false })); }, []);
  if (!cfg?.active || cfg.book_product_id !== productId) return null;
  const { book, tee } = cfg;
  const needSize = tee.sizes?.length > 0, needColour = tee.colours?.length > 0;
  const ready = (!needSize || size) && (!needColour || colour);

  const add = () => {
    if (!ready) { toast.error(`Pick a ${!size && needSize ? "size" : "colour"} for the tee first.`); return; }
    addBundle({
      kind: "book-tee", name: cfg.name, price: cfg.price, image: book.image,
      components: [{ product_id: book.id, name: book.name }, { product_id: tee.id, name: tee.name, size, colour }],
    });
    toast.success("Bundle added — the perfect pair 🐾");
  };

  return (
    <div className="mt-8 rounded-3xl bg-[#FFD93D]/20 border-2 border-[#FFD93D] p-5 sm:p-6 relative overflow-hidden" data-testid="book-tee-upsell">
      <span className="absolute -top-1 right-5 rotate-6 bg-[#FF6B9D] text-white text-xs font-display font-bold px-3 py-1 rounded-b-xl" data-testid="upsell-saving">Save {formatAUD(cfg.saving)}</span>
      <p className="font-hand text-xl text-[#FF6B9D]">Make it a matching set</p>
      <h3 className="font-display font-extrabold text-xl sm:text-2xl text-[#19324A] leading-tight">{cfg.name}</h3>
      <div className="mt-4 flex items-center gap-3">
        <img src={book.image} alt={book.name} className="w-20 h-20 rounded-2xl object-cover ring-2 ring-white soft-shadow" />
        <span className="font-display font-extrabold text-2xl text-[#19324A]/50">+</span>
        <img src={tee.image} alt={tee.name} className="w-20 h-20 rounded-2xl object-cover ring-2 ring-white soft-shadow" />
        <div className="ml-auto text-right">
          <p className="text-sm text-[#19324A]/50 line-through">{formatAUD(cfg.separate_total)}</p>
          <p className="font-display font-extrabold text-3xl text-[#19324A]" data-testid="upsell-price">{formatAUD(cfg.price)}</p>
        </div>
      </div>
      <p className="mt-3 text-sm text-[#19324A]/70">The book plus the <strong>{tee.name}</strong> so the little one can dress like the story.</p>
      {needColour && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-[#19324A]/60 mr-1">Tee colour:</span>
          {tee.colours.map((c) => <button key={c} type="button" onClick={() => setColour(c)} data-testid={`upsell-colour-${c}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${colour === c ? "bg-white border-[#FF6B9D] text-[#19324A]" : "bg-white/60 border-transparent text-[#19324A]/70 hover:border-[#FF6B9D]"}`}>{c}</button>)}
        </div>
      )}
      {needSize && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-[#19324A]/60 mr-1">Tee size:</span>
          {tee.sizes.map((s) => <button key={s} type="button" onClick={() => setSize(s)} data-testid={`upsell-size-${s}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${size === s ? "bg-white border-[#FF6B9D] text-[#19324A]" : "bg-white/60 border-transparent text-[#19324A]/70 hover:border-[#FF6B9D]"}`}>{s}</button>)}
        </div>
      )}
      <button type="button" onClick={add} data-testid="upsell-add" className={`mt-5 w-full btn-pink ${ready ? "" : "opacity-70"}`}>
        {ready ? <Check className="w-5 h-5" /> : <Gift className="w-5 h-5" />} Add both to bag — {formatAUD(cfg.price)}
      </button>
    </div>
  );
}
